import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { CERTIFICATIONS, COMPLIANCE_ITEMS } from "@/data"
import { useSEO } from "@/hooks/useSEO"
import { CTASection } from "@/sections/CTASection"
import { createFileRoute } from "@tanstack/react-router"
import { AlertTriangle, Globe, Lock, Shield } from "lucide-react"

export const Route = createFileRoute("/compliance")({
  component: CompliancePage,
})

const RISK_CONTROLS = [
  {
    icon: Shield,
    title: "AML Screening",
    description:
      "Every transaction screened against OFAC, EU, and UN sanctions lists in real-time.",
    color: "bg-blue-light",
    iconColor: "text-blue",
  },
  {
    icon: AlertTriangle,
    title: "Transaction Monitoring",
    description: "Behavioral analytics flag anomalous patterns before they become problems.",
    color: "bg-amber-light",
    iconColor: "text-amber-DEFAULT",
  },
  {
    icon: Globe,
    title: "Country Controls",
    description: "Granular country-level blocklists and whitelists enforced at the API layer.",
    color: "bg-green-light",
    iconColor: "text-green-DEFAULT",
  },
  {
    icon: Lock,
    title: "KYC/KYB Verification",
    description: "Document verification and business registry checks powered by leading providers.",
    color: "bg-rose-light",
    iconColor: "text-rose-DEFAULT",
  },
]

function CompliancePage() {
  useSEO({
    title: "Compliance & Risk",
    description:
      "Orchestrate maintains PCI DSS Level 1, SOC 2 Type II, GDPR, and ISO 27001 compliance. Built-in AML screening, KYC verification, and country-level controls.",
    keywords: "PCI DSS, GDPR, SOC 2, AML screening, KYC, payment compliance",
    canonical: "https://orchestrate.global/compliance",
  })

  return (
    <>
      {/* Hero */}
      <section className="bg-ink relative overflow-hidden pt-32 pb-20">
        <div
          className="grid-bg pointer-events-none absolute inset-0 opacity-20"
          aria-hidden="true"
        />
        <div
          className="bg-blue/15 pointer-events-none absolute top-1/2 left-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatedSection>
            <SectionLabel label="Compliance & Risk" className="mb-5" />
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <h1 className="font-display mb-6 max-w-3xl text-5xl leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
              Security and compliance <em className="text-blue-mid not-italic">by default.</em>
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <p className="max-w-xl text-xl text-white/50">
              Every API call is backed by enterprise-grade compliance infrastructure, not an
              afterthought.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Certifications */}
      <section className="border-border border-b bg-white py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p className="text-muted mb-10 text-center text-xs font-semibold tracking-widest uppercase">
            Our certifications
          </p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {CERTIFICATIONS.map(({ label, sub }) => (
              <div
                key={label}
                className="bg-surface border-border hover:border-blue-mid hover:bg-blue-light rounded-xl border p-5 text-center transition-all duration-200"
              >
                <p className="mb-0.5 text-sm font-semibold">{label}</p>
                <p className="text-muted text-xs">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk controls */}
      <section className="bg-surface py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-16 text-center">
              <SectionLabel label="Risk Controls" centered className="mb-5" />
              <h2 className="font-display text-4xl tracking-tight sm:text-5xl">
                Built-in protection
              </h2>
            </div>
          </AnimatedSection>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {RISK_CONTROLS.map((ctrl, i) => {
              const Icon = ctrl.icon
              return (
                <AnimatedSection key={ctrl.title} delay={i * 0.1}>
                  <div className="border-border rounded-2xl border bg-white p-6 transition-all duration-250 hover:-translate-y-1.5 hover:shadow-xl">
                    <div
                      className={`h-11 w-11 ${ctrl.color} mb-5 flex items-center justify-center rounded-xl`}
                    >
                      <Icon className={`h-5 w-5 ${ctrl.iconColor}`} />
                    </div>
                    <h3 className="mb-2 text-base font-semibold">{ctrl.title}</h3>
                    <p className="text-muted text-sm leading-relaxed">{ctrl.description}</p>
                  </div>
                </AnimatedSection>
              )
            })}
          </div>
        </div>
      </section>

      {/* Country classifications */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-12 text-center">
              <SectionLabel label="Country Controls" centered className="mb-5" />
              <h2 className="font-display text-4xl tracking-tight sm:text-5xl">Global coverage</h2>
            </div>
          </AnimatedSection>
          <div className="space-y-4">
            {COMPLIANCE_ITEMS.map((item, i) => (
              <AnimatedSection key={item.id} delay={i * 0.1}>
                <div className="bg-surface border-border rounded-2xl border p-6">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-base font-semibold">{item.label}</h3>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                        item.status === "blocked"
                          ? "bg-rose-light text-rose-DEFAULT"
                          : item.status === "edd"
                            ? "bg-amber-light text-amber-DEFAULT"
                            : "bg-green-light text-green-DEFAULT"
                      }`}
                    >
                      {item.status === "blocked"
                        ? "BLOCKED"
                        : item.status === "edd"
                          ? "EDD REQUIRED"
                          : "CONTROLLED"}
                    </span>
                  </div>
                  <p className="text-muted text-sm">{item.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}
